/* eslint react/prop-types: 0 */
import React from 'react'
import classnames from 'classnames'
import { getPreClones, lazyEndIndex, lazyStartIndex } from './utils.js'

// given specifications/props for a slide, fetch all the classes that need to be applied to the slide
const getSlideClasses = (spec) => {
  let index

  if (spec.rtl) {
    index = spec.slideCount - 1 - spec.index
  } else {
    // eslint-disable-next-line prefer-destructuring
    index = spec.index
  }
  const slickCloned = index < 0 || index >= spec.slideCount
  const slickActive = spec.currentSlide <= index && index < spec.currentSlide + spec.slidesToShow

  let focusedSlide
  if (spec.targetSlide < 0) {
    focusedSlide = spec.targetSlide + spec.slideCount
  } else if (spec.targetSlide >= spec.slideCount) {
    focusedSlide = spec.targetSlide - spec.slideCount
  } else {
    focusedSlide = spec.targetSlide
  }
  return {
    'slick-slide': true,
    'slick-active': slickActive,
    'slick-cloned': slickCloned,
    'slick-current': index === focusedSlide, // dubious in case of RTL
  }
}

const getSlideStyle = (spec) => {
  const style = {}

  style.width = spec.slideWidth

  return style
}

const getKey = (child, fallbackKey) => child.key || fallbackKey

const renderSlides = (spec) => {
  let key
  const slides = []
  const preCloneSlides = []
  const postCloneSlides = []
  const childrenCount = React.Children.count(spec.children)
  const startIndex = lazyStartIndex(spec)
  const endIndex = lazyEndIndex(spec)

  React.Children.forEach(spec.children, (elem, index) => {
    let child

    // in case of lazyLoad, whether we want to fetch the slide
    if (spec.lazyLoadedList.indexOf(index) >= 0) {
      child = elem
    } else {
      child = <div />
    }
    const childStyle = getSlideStyle({ ...spec, index })
    const slideClass = child.props.className || ''
    let slideClasses = getSlideClasses({ ...spec, index })
    // push a cloned element of the desired slide
    slides.push(
      React.cloneElement(child, {
        key: `original${getKey(child, index)}`,
        'data-index': index,
        className: classnames(slideClasses, slideClass),
        tabIndex: '-1',
        'aria-hidden': !slideClasses['slick-active'],
        style: { outline: 'none', ...(child.props.style || {}), ...childStyle },
        onClick: (e) => {
          // eslint-disable-next-line no-unused-expressions
          child.props && child.props.onClick && child.props.onClick(e)
        },
      }),
    )

    // if slide needs to be precloned or postcloned
    if (spec.infinite) {
      const preCloneNo = childrenCount - index
      if (preCloneNo <= getPreClones(spec) && childrenCount !== spec.slidesToShow) {
        key = -preCloneNo
        if (key >= startIndex) {
          child = elem
        }
        slideClasses = getSlideClasses({ ...spec, index: key })
        preCloneSlides.push(
          React.cloneElement(child, {
            key: `precloned${getKey(child, key)}`,
            'data-index': key,
            tabIndex: '-1',
            className: classnames(slideClasses, slideClass),
            'aria-hidden': !slideClasses['slick-active'],
            style: { ...(child.props.style || {}), ...childStyle },
            onClick: (e) => {
              // eslint-disable-next-line no-unused-expressions
              child.props && child.props.onClick && child.props.onClick(e)
            },
          }),
        )
      }

      if (childrenCount !== spec.slidesToShow) {
        key = childrenCount + index
        if (key < endIndex) {
          child = elem
        }
        slideClasses = getSlideClasses({ ...spec, index: key })
        postCloneSlides.push(
          React.cloneElement(child, {
            key: `postcloned${getKey(child, key)}`,
            'data-index': key,
            tabIndex: '-1',
            className: classnames(slideClasses, slideClass),
            'aria-hidden': !slideClasses['slick-active'],
            style: { ...(child.props.style || {}), ...childStyle },
            onClick: (e) => {
              // eslint-disable-next-line no-unused-expressions
              child.props && child.props.onClick && child.props.onClick(e)
            },
          }),
        )
      }
    }
  })

  return preCloneSlides.concat(slides, postCloneSlides)
}

class Track extends React.PureComponent {
  node = null

  handleRef = (ref) => {
    this.node = ref
  }

  render() {
    const slides = renderSlides(this.props)
    const { onMouseEnter, onMouseOver, onMouseLeave } = this.props
    const mouseEvents = { onMouseEnter, onMouseOver, onMouseLeave }
    return (
      <div
        ref={this.handleRef}
        className="slick-track"
        style={this.props.trackStyle}
        {...mouseEvents}
      >
        {slides}
      </div>
    )
  }
}

export default Track
